import type { WeekdayProfileEntry } from '@/api/revenue'
import { formatValue, NARROW_QUERY, useMediaQuery, type FormatOptions } from '@/design'
import { ChartDataTable, ChartYGrid, useChartCursor } from '@/design/chartParts'
import { niceMax } from '@/design/TrendChart'

import { WEEKDAY_LONG, WEEKDAY_SHORT } from '../format'
import { ChartReadout } from '../ChartReadout'

const HEIGHT = 200
const PAD = { left: 60, right: 8, top: 10, bottom: 28 }

/**
 * Average revenue per weekday, this period beside the comparison's: two series on one
 * axis, grouped by weekday, with a legend and a readout per weekday (pointer or arrow
 * keys). With no comparison, one series and no legend box.
 */
export function WeekdayProfile({
  profile,
  money,
  currentLabel,
  previousLabel,
}: {
  profile: readonly WeekdayProfileEntry[]
  money: FormatOptions
  currentLabel: string
  /** Null when there is no comparison to draw. */
  previousLabel: string | null
}) {
  const viewWidth = useMediaQuery(NARROW_QUERY) ? 360 : 640
  const innerWidth = viewWidth - PAD.left - PAD.right
  const innerHeight = HEIGHT - PAD.top - PAD.bottom
  const step = innerWidth / 7
  const cursor = useChartCursor(7, viewWidth, (position) => Math.floor((position - PAD.left) / step))
  const { active, readoutId } = cursor

  const values = profile.flatMap((entry) => [entry.averageRevenue, ...(previousLabel && entry.previousAverageRevenue !== null ? [entry.previousAverageRevenue] : [])])
  const max = niceMax(Math.max(0, ...values))
  const y = (value: number) => PAD.top + innerHeight - (Math.max(0, value) / max) * innerHeight
  const fmt = (value: number | null, compact = false) => (value === null ? '—' : formatValue(value, 'currency', { ...money, compact }))
  const groupWidth = Math.min(64, step * 0.7)
  const barWidth = previousLabel ? (groupWidth - 2) / 2 : groupWidth
  const activeEntry = active === null ? null : profile[active]

  return (
    <figure className="ci-trend rv-weekday">
      <div className="ci-trend__plot" aria-label="Average revenue per weekday. Use the arrow keys to read each day." {...cursor.plotProps}>
        <svg viewBox={`0 0 ${viewWidth} ${HEIGHT}`} className="ci-trend__svg" aria-hidden="true" focusable="false" {...cursor.svgProps}>
          <ChartYGrid max={max} ticks={4} left={PAD.left} right={viewWidth - PAD.right} y={y} label={(value) => fmt(value, true)} />
          {profile.map((entry, index) => {
            const left = PAD.left + index * step + (step - groupWidth) / 2
            return (
              <g key={entry.weekday} data-testid="weekday-group">
                <rect className="rv-weekday__bar" data-series="current" x={left} y={y(entry.averageRevenue)} width={barWidth} height={PAD.top + innerHeight - y(entry.averageRevenue)} rx={2} />
                {previousLabel && entry.previousAverageRevenue !== null ? (
                  <rect
                    className="rv-weekday__bar"
                    data-series="previous"
                    x={left + barWidth + 2}
                    y={y(entry.previousAverageRevenue)}
                    width={barWidth}
                    height={PAD.top + innerHeight - y(entry.previousAverageRevenue)}
                    rx={2}
                  />
                ) : null}
                <text className="ci-trend__x" x={left + groupWidth / 2} y={HEIGHT - 8} textAnchor="middle">
                  {WEEKDAY_SHORT[entry.weekday]}
                </text>
              </g>
            )
          })}
        </svg>
        <ChartReadout id={readoutId} left={active === null ? null : ((PAD.left + (active + 0.5) * step) / viewWidth) * 100}>
          {activeEntry ? (
            <>
              <strong>{WEEKDAY_LONG[activeEntry.weekday]}</strong>
              <span>
                {currentLabel}: {fmt(activeEntry.averageRevenue)} ({activeEntry.days} {activeEntry.days === 1 ? 'day' : 'days'})
              </span>
              {previousLabel ? (
                <span>
                  {previousLabel}: {fmt(activeEntry.previousAverageRevenue)}
                </span>
              ) : null}
            </>
          ) : null}
        </ChartReadout>
      </div>
      {previousLabel ? (
        <figcaption className="rv-legend">
          <span className="rv-legend__item">
            <span className="rv-legend__swatch" data-series="current" aria-hidden="true" />
            {currentLabel}
          </span>
          <span className="rv-legend__item">
            <span className="rv-legend__swatch" data-series="previous" aria-hidden="true" />
            {previousLabel}
          </span>
        </figcaption>
      ) : null}
      <ChartDataTable
        title="Average revenue per weekday"
        columns={['Weekday', currentLabel, ...(previousLabel ? [previousLabel] : [])]}
        rows={profile.map((entry) => ({
          key: String(entry.weekday),
          name: WEEKDAY_LONG[entry.weekday],
          cells: [fmt(entry.averageRevenue), ...(previousLabel ? [fmt(entry.previousAverageRevenue)] : [])],
        }))}
      />
    </figure>
  )
}
